/**
 * 리포트 투표 추적 훅
 * 
 * 리포트 상세 페이지에서 투표하는 기능
 * - start()는 세션 스토리지를 확인하여 저장된 의도가 있으면 자동 복원
 * - 로그인 후 리다이렉트되어도 같은 의도 유지
 * - 로그인 의도는 자동으로 부모 의도 연결 (parentIntentType)
 * 
 * @example
 * ```tsx
 * function ReportDetailPage({ reportId }) {
 *   const vote = useVote({ report_id: reportId, option: 'o' });
 *   const loginIntent = useLoginIntent();
 *   const isLoggedIn = useAuth().isLoggedIn;
 *   
 *   const handleVote = async (selectedOption: 'o' | 'x' | 'a' | 'b' | 'c' | 'd') => {
 *     vote.start({ option: selectedOption }); // 의도 시작
 *     
 *     if (!isLoggedIn) {
 *       // 로그인 의도 시작 (부모 의도 자동 연결)
 *       loginIntent.start(
 *         { redirectUrl: router.asPath },
 *         { parentIntentType: 'vote_click' }  // 👈 타입으로 부모 의도 자동 조회
 *       );
 *       router.push('/login');
 *       return;
 *     }
 *     
 *     try {
 *       await apiVote(reportId, selectedOption);
 *       vote.success();
 *     } catch (error) {
 *       vote.fail('backend_5xx');
 *     }
 *   };
 *   
 *   return <button onClick={() => handleVote('o')}>찬성</button>;
 * }
 * ```
 */

import { useCallback } from 'react';
import { track } from '../../../../core/client';
import { useIntent } from '../useIntent';

export interface VoteContext {
    report_id: number;
    option: 'o' | 'x' | 'a' | 'b' | 'c' | 'd';
}

export interface VoteActions {
    start: (context?: VoteContext) => void;
    success: () => void;
    fail: (reason?: string) => void;
}

/**
 * 리포트 투표 추적 훅
 */
export function useVote(
    initialContext?: VoteContext
): VoteActions {
    const { report_id, option } = initialContext || {};

    const intent = useIntent<VoteContext>({
        type: 'vote_click',
        requiredGate: 'login',
        onStart: ({ intent_id, context, parent_intent_id, required_gate }) => {
            track('vote_click_start', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                option: context?.option || option || 'o',
                parent_intent_id,
                required_gate,
            });
        },
        onSuccess: ({ intent_id, context, latency_ms }) => {
            track('vote_click_success', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                option: context?.option || option || 'o',
                latency_ms,
            });
        },
        onFail: ({ intent_id, context, fail_reason }) => {
            track('vote_click_fail', {
                intent_id,
                report_id: context?.report_id || report_id || 0,
                option: context?.option || option || 'o',
                fail_reason,
            });
        },

    });

    /**
     * 투표 의도 시작
     */
    const start = useCallback((context?: VoteContext) => {
        intent.start(context || initialContext);
    }, [intent, initialContext]);

    /**
     * 투표 성공
     */
    const success = useCallback(() => {
        intent.success();
    }, [intent]);

    /**
     * 투표 실패
     */
    const fail = useCallback((reason?: string) => {
        intent.fail(reason);
    }, [intent]);

    return {
        start,
        success,
        fail,
    };
}


